import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * What a start-time field shows that its `value` does not hold yet: text still
 * being typed, or text it refused (it says why, beside it). `null` once the
 * two agree.
 */
export type TimeEntryState = 'typing' | 'refused' | null;

/** The field's half of `useTimeEntry`, handed to a restricted `TimePicker`. */
export interface TimeEntryBinding {
  /** The field says what it shows that its `value` does not hold. */
  report: (state: TimeEntryState) => void;
  /** Where the field keeps its "take the typed text now" while it is mounted. */
  commitRef: { current: (() => string | null) | null };
  /**
   * The same schedule's duration field: it says when it shows a duration the
   * form cannot take (not whole, or out of range), and keeps here its "check
   * what is typed now", `false` when it cannot be taken.
   */
  duration: {
    report: (refused: boolean) => void;
    commitRef: { current: (() => boolean) | null };
  };
}

export interface TimeEntry {
  state: TimeEntryState;
  /**
   * Takes the text the field shows, exactly as Enter would, and returns the
   * start the field then shows: the typed time, or when nothing was typed the
   * time on screen (`''` if it shows none). `null` when the text is refused,
   * or the duration shown cannot be taken (the field says why), `undefined`
   * when no start field is on screen.
   */
  commit: () => string | null | undefined;
  binding: TimeEntryBinding;
}

/**
 * A form's side of its start-time field (NOT-781).
 *
 * Typed text only becomes the field's value once it is taken: on Enter, on
 * blur, or when the form calls `commit()`. Until then the form's start time is
 * the last one taken, not the one on screen, and a form that saved would send
 * a time nobody is looking at. `state` says when that is so; `commit()` is how
 * Save takes the typed text first — a tap does not blur the field on every
 * browser, and a save must not depend on it.
 */
export function useTimeEntry(): TimeEntry {
  const [startState, setStartState] = useState<TimeEntryState>(null);
  const [durationRefused, setDurationRefused] = useState(false);
  const commitRef = useRef<(() => string | null) | null>(null);
  const durationCommitRef = useRef<(() => boolean) | null>(null);
  const binding = useMemo<TimeEntryBinding>(
    () => ({
      report: setStartState,
      commitRef,
      duration: { report: setDurationRefused, commitRef: durationCommitRef },
    }),
    [],
  );
  // Both fields are checked, so each says why it cannot be taken.
  const commit = useCallback(() => {
    const durationTaken = durationCommitRef.current?.() ?? true;
    const start = commitRef.current?.();
    return durationTaken ? start : null;
  }, []);
  // A duration the form cannot take blocks Save just as a refused start does.
  const state: TimeEntryState = durationRefused ? 'refused' : startState;
  return { state, commit, binding };
}

/**
 * One click on Save saves the start time on screen (NOT-781).
 *
 * `request()` takes the field's typed text first. Refused, it returns `false`
 * and nothing is saved; the field says why. Taken, `save` runs once the form
 * has caught up with it: a render later — so state a form keeps in step with
 * the start time through an effect of its own has landed — and on the first
 * render after that where `ready` holds, i.e. whatever checks the time (the
 * technician availability check) has answered. Until then `waiting` is true,
 * for the button to show.
 *
 * A press saves the form as it was pressed on. Callers lock the form while
 * `waiting` (`inert`), as for any submit in progress, so nothing can be
 * changed under the press. `inputs` — the schedule as the form would send it —
 * is the backstop: if it changes anyway while the press waits (another time,
 * a technician swapped, the visit reloaded), the press is dropped rather than
 * saving something nobody pressed Save for.
 *
 * Save used to switch itself off for those same moments instead: while text
 * was typed, and while availability was re-checked for the new time. The
 * click that took the typed time (its press blurs the field) then landed on a
 * disabled button, and saving took a second click.
 */
export function useTimeEntrySave({
  entry,
  inputs,
  ready,
  save,
}: {
  entry: TimeEntry;
  /** Everything the user chose that the save sends, as one key. */
  inputs: string;
  /** Nothing that checks the start time is still working on it. */
  ready: boolean;
  save: () => void;
}): { request: () => boolean; waiting: boolean } {
  const { commit, state } = entry;
  // `taken`: pressed, the typed time handed to the form on this render.
  // `pressedOn`: the schedule as that press left it, typed time included.
  const [pending, setPending] = useState<
    { stage: 'taken' } | { stage: 'settled'; pressedOn: string } | null
  >(null);
  const request = useCallback(() => {
    if (commit() === null) return false;
    setPending({ stage: 'taken' });
    return true;
  }, [commit]);
  useEffect(() => {
    if (!pending) return;
    if (pending.stage === 'taken') {
      setPending({ stage: 'settled', pressedOn: inputs });
      return;
    }
    // Typed over, or anything else changed, while waiting: not what was pressed.
    if (state !== null || inputs !== pending.pressedOn) {
      setPending(null);
      return;
    }
    if (!ready) return;
    setPending(null);
    save();
  }, [pending, state, inputs, ready, save]);
  return { request, waiting: pending !== null };
}
